import api from './api';
import { captureEvent } from '../utils/posthog';

/**
 * Service API pour la gestion des formations
 * @module services/formations
 */

/**
 * Charger des formations dans une organisation (avec limite freemium)
 * @param {Object} params - Paramètres de chargement
 * @param {string} params.organizationId - ID de l'organisation
 * @param {Array<Object>} params.formations - Liste des formations à charger
 * @param {string} params.formations[].code - Code de la formation (RNCP, CPF, etc.)
 * @param {string} params.formations[].title - Titre de la formation
 * @param {string} [params.formations[].description] - Description
 * @param {string} [params.formations[].level] - Niveau (3, 4, 5, 6, 7)
 * @param {number} [params.formations[].duration] - Durée en heures
 * @param {Array<Object>} [params.formations[].competences] - Compétences associées
 * @param {string} [params.formations[].rncpCode] - Code RNCP
 * @param {Object} [params.formations[].metadata] - Métadonnées
 * @returns {Promise<Object>} Résultat du chargement
 */
export async function loadFormations({ organizationId, formations }) {
  try {
    const response = await api.post('/formations/load', {
      organizationId,
      formations
    });

    // Success - formations loaded
    return {
      success: true,
      data: response.data.formations,
      stats: {
        loaded: response.data.loaded,
        existing: response.data.existing,
        total: response.data.total
      },
      message: response.data.message
    };
  } catch (error) {
    console.error('Erreur chargement formations:', error);

    // Check if freemium limit reached
    if (error.response?.status === 403 && error.response?.data?.upgradeRequired) {
      return {
        success: false,
        freemiumLimitReached: true,
        error: error.response.data.message,
        limit: error.response.data.limit,
        current: error.response.data.current,
        upgradeRequired: true
      };
    }

    return {
      success: false,
      error: error.response?.data?.message || 'Erreur lors du chargement',
      details: error.response?.data
    };
  }
}

/**
 * Récupérer la liste des formations d'une organisation
 * @param {Object} params - Paramètres de récupération
 * @param {string} params.organizationId - ID de l'organisation
 * @param {number} [params.page=1] - Numéro de page
 * @param {number} [params.limit=20] - Nombre d'éléments par page
 * @param {boolean} [params.activeOnly=true] - Récupérer uniquement les formations actives
 * @returns {Promise<Object>} Liste des formations avec pagination
 */
export async function getFormations({ organizationId, page = 1, limit = 20, activeOnly = true } = {}) {
  try {
    const params = new URLSearchParams({
      organizationId,
      page: page.toString(),
      limit: limit.toString(),
      activeOnly: activeOnly.toString()
    });

    const response = await api.get(`/formations?${params.toString()}`);

    return {
      success: true,
      data: response.data.formations,
      pagination: {
        page: response.data.page,
        limit: response.data.limit,
        total: response.data.total,
        totalPages: response.data.totalPages
      }
    };
  } catch (error) {
    console.error('Erreur récupération formations:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Erreur lors de la récupération',
      details: error.response?.data
    };
  }
}

/**
 * Récupérer les détails d'une formation par ID
 * @param {string} formationId - ID UUID de la formation
 * @returns {Promise<Object>} Détails de la formation
 */
export async function getFormation(formationId) {
  try {
    const response = await api.get(`/formations/${formationId}`);

    return {
      success: true,
      data: response.data.formation
    };
  } catch (error) {
    console.error('Erreur récupération formation:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Formation introuvable',
      details: error.response?.data
    };
  }
}

/**
 * Supprimer une formation (soft delete)
 * @param {string} formationId - ID UUID de la formation
 * @returns {Promise<Object>} Résultat de la suppression
 */
export async function deleteFormation(formationId) {
  try {
    const response = await api.delete(`/formations/${formationId}`);

    return {
      success: true,
      message: response.data.message
    };
  } catch (error) {
    console.error('Erreur suppression formation:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Erreur lors de la suppression',
      details: error.response?.data
    };
  }
}

/**
 * Extraire les codes de compétences d'une formation
 * @param {Object} formation - Objet formation
 * @returns {Array<string>} Liste des codes de compétences
 */
export function getCompetenceCodes(formation) {
  if (!formation || !Array.isArray(formation.competences)) return [];
  return formation.competences.map(c => c.code || c.id).filter(Boolean);
}

/**
 * Compter le nombre total de compétences uniques
 * @param {Array<Object>} formations - Liste des formations
 * @returns {number} Nombre de compétences uniques
 */
export function countUniqueCompetences(formations) {
  if (!Array.isArray(formations)) return 0;

  const uniqueCodes = new Set();

  formations.forEach(formation => {
    const codes = getCompetenceCodes(formation);
    codes.forEach(code => uniqueCodes.add(code));
  });

  return uniqueCodes.size;
}

/**
 * Grouper les formations par niveau
 * @param {Array<Object>} formations - Liste des formations
 * @returns {Object} Formations groupées par niveau
 */
export function groupByLevel(formations) {
  if (!Array.isArray(formations)) return {};

  return formations.reduce((acc, formation) => {
    const level = formation.level || 'Non spécifié';
    if (!acc[level]) acc[level] = [];
    acc[level].push(formation);
    return acc;
  }, {});
}

/**
 * Calculer la durée totale des formations (en heures)
 * @param {Array<Object>} formations - Liste des formations
 * @returns {number} Durée totale en heures
 */
export function calculateTotalDuration(formations) {
  if (!Array.isArray(formations)) return 0;

  return formations.reduce((total, formation) => {
    return total + (formation.duration || 0);
  }, 0);
}

/**
 * Vérifier si l'utilisateur peut charger plus de formations (freemium)
 * @param {number} currentCount - Nombre actuel de formations
 * @param {number} newCount - Nombre de nouvelles formations à charger
 * @param {boolean} isPremium - Si l'utilisateur est premium
 * @param {number} [freemiumLimit=10] - Limite freemium
 * @returns {Object} Résultat de la vérification
 */
export function canLoadMoreFormations(currentCount, newCount, isPremium, freemiumLimit = 10) {
  if (isPremium) {
    return {
      canLoad: true,
      reason: 'premium'
    };
  }

  const totalCount = currentCount + newCount;

  if (totalCount <= freemiumLimit) {
    return {
      canLoad: true,
      remaining: freemiumLimit - totalCount,
      limit: freemiumLimit
    };
  }

  return {
    canLoad: false,
    exceeded: totalCount - freemiumLimit,
    limit: freemiumLimit,
    upgradeRequired: true
  };
}

/**
 * Formater une liste de formations pour l'import
 * @param {Array<Object>} rawFormations - Données brutes
 * @returns {Array<Object>} Formations formatées
 */
export function formatFormationsForImport(rawFormations) {
  if (!Array.isArray(rawFormations)) return [];

  return rawFormations.map(formation => ({
    code: formation.code || formation.id || '',
    title: formation.title || formation.name || '',
    description: formation.description || '',
    level: formation.level || formation.niveau || '',
    duration: parseInt(formation.duration || formation.duree) || null,
    competences: Array.isArray(formation.competences) ? formation.competences : [],
    rncpCode: formation.rncpCode || formation.rncp || '',
    metadata: formation.metadata || {}
  }));
}

export default {
  loadFormations,
  getFormations,
  getFormation,
  deleteFormation,
  getCompetenceCodes,
  countUniqueCompetences,
  groupByLevel,
  calculateTotalDuration,
  canLoadMoreFormations,
  formatFormationsForImport
};
